import React from 'react';
import { useState, useRef, useEffect } from "react";
import CustomDatePicker from './CustomDatePicker';
import { FiChevronDown } from "react-icons/fi";


interface CustomInputProps {
    label?: string;
    name: string;
    placeholder?: string;
    value: string;
    type?: string; // "text" | "date" | "textarea"
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
    error?: string;
    icon?: string;
    min?: string;
    borderBotttom?: boolean;
    disabled?: boolean;
}

const ChevronDown = FiChevronDown as any;

const formatDateForDisplay = (value: string) => {
    if (!value) return "";
    const [year, month, day] = value.split("-");
    return `${month}-${day}-${year}`;
};


const CustomInput: React.FC<CustomInputProps> = ({
    label,
    name,
    placeholder,
    value,
    type = "text",
    onChange,
    error,
    icon,
    borderBotttom,
    min,
    disabled,
}) => {
    const isTextarea = type === "textarea";
    const isWaivera = placeholder === "Waivera";

    const isDate = type === "date";
    const [open, setOpen] = useState(false);
    const wrapperRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    return (
        <div style={{ width: "100%" }} ref={wrapperRef}>
            <div
                className={`
                ${type === "textarea" ? "h-full" : "h-[48px]"} flex items-center relative ps-3
                    border-x-[1px] border-[#F5F6F7]
                    ${disabled ? "bg-[#F4F4F4]" : ""}
                    ${borderBotttom ? "border-y-2" : "border-t-2"}
                `}


            >
                {/* Left icon */}
                {icon && (
                    <div
                        className={`absolute left-3 ${isTextarea ? "top-[20px]" : "top-1/2"} 
                             ${isWaivera ? "w-[30px] h-[30px]" : "w-[25px] h-[25px]"}  -translate-y-1/2 flex items-center`}
                    // style={{ width: "25px", height: "25px" }}
                    >
                        <img
                            src={icon}
                            alt="icon"
                            className={`w-full h-full ${name === "title" ? "grayscale opacity-45" : ""}`}
                        />
                    </div>
                )}

                {/* TEXTAREA */}
                {isTextarea ? (
                    <textarea
                        name={name}
                        placeholder={placeholder}
                        value={value}
                        onChange={onChange}
                        className="w-full !text-[13px] 2xl:!text-[16px] !font-normal"
                        rows={3}
                        style={{
                            width: "100%",
                            paddingLeft: icon ? "2.5rem" : "0",
                            paddingRight: "0.5rem",
                            paddingTop: "0.5rem",
                            paddingBottom: "0.5rem",
                            border: "none",
                            background: "transparent",
                            outline: "none",
                            boxShadow: "none",
                            color: "#747474",
                            fontWeight: "400",
                        }}
                    />
                ) : isDate ? (
                    <>
                        {/* DATE INPUT */}
                        <input
                            readOnly
                            value={formatDateForDisplay(value)}
                            placeholder={placeholder}
                            onClick={() => setOpen(!open)}
                            className="w-full !text-[13px] 2xl:!text-[16px] !font-normal"
                            style={{
                                width: "100%",
                                paddingLeft: icon ? "2.5rem" : "0",
                                paddingRight: "0.5rem",
                                paddingTop: "0.5rem",
                                paddingBottom: "0.5rem",
                                border: "none",
                                background: "transparent",
                                outline: "none",
                                boxShadow: "none",
                                color: "#747474",
                                fontWeight: "400",
                                cursor: "pointer",
                            }}
                            disabled={disabled}
                        />

                        {/* CALENDAR */}
                        {open && (
                            <div
                                className="fixed z-[9999]"
                                style={{
                                    top: wrapperRef.current?.getBoundingClientRect().bottom ?? 0,
                                    left: wrapperRef.current?.getBoundingClientRect().left ?? 0,
                                }}
                            >
                                <CustomDatePicker
                                    value={value}
                                    onChange={(date) => {
                                        onChange({
                                            target: { name, value: date },
                                        } as any);
                                        setOpen(false);
                                    }}
                                />
                            </div>
                        )}
                    </>
                ) : (
                    /* NORMAL INPUT */
                    <input
                        type={type}
                        name={name}
                        placeholder={placeholder}
                        value={value}
                        min={min}
                        onChange={onChange}
                        className={`${type === "date" ? "custom-date-input" : ""} !text-[13px] 2xl:!text-[16px] !font-normal`}
                        style={{
                            width: "100%",
                            paddingLeft: icon ? "2.5rem" : "0",
                            paddingRight: "0.5rem",
                            paddingTop: "0.5rem",
                            paddingBottom: "0.5rem",
                            border: "none",
                            background: "transparent",
                            outline: "none",
                            boxShadow: "none",
                            color: "#747474",
                            fontWeight: "400",
                            cursor: type === "date" ? "pointer" : "text",
                        }}
                        disabled={disabled}
                    // onClick={(e) => {
                    //     if (type === "date") {
                    //         (e.currentTarget as HTMLInputElement).showPicker?.();
                    //     }
                    // }}
                    />
                )}

                {
                    isDate &&
                    <ChevronDown
                        className={`absolute right-3 transition-transform duration-200 pointer-events-none 
                        ${open ? "rotate-180" : ""}`}
                        size={18}
                        color="#777"
                    />
                }
            </div>


            {/* Error */}
            {error && (
                <p
                    style={{
                        color: "#ef4444",
                        fontSize: "0.875rem",
                        paddingLeft: "40px",
                    }}
                >
                    {error}
                </p>
            )}
        </div>
    );
};

export default CustomInput;
